import React, { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { getCompanyList, getAllMessagingUsers, getSession } from "@api/api";
import { View, useWindowDimensions, Platform, Dimensions } from "react-native";
import { Pressable } from "react-native-web";
import { PIN_NOTE, NOTE_DROPPED, SWITCH_COMPANY_REQUEST, CLEAR_COMPANY, SHOW_ADD_COMPANY_MODAL, HIDE_PAYMENT_REQUIRED, SHOW_LOADING_MODAL, HIDE_LOADING_MODAL } from "@reduxLocal/actions/types";
import { textHelp } from "../HeaderBar";
import { themeSelector } from "@reduxLocal/selectors/themeSelector";
import { useQueryClient } from "@tanstack/react-query";
import { useDispatch, useSelector } from 'react-redux';
import { useNavigation } from "@react-navigation/native";
import { abortAllRequests } from "@utils/abortController";
import { FontAwesome, MaterialIcons } from "@expo/vector-icons";
import toTitleCase from "@hooks/toTitleCase";
import { DefaultRootState } from "@reduxLocal/persistState";
import { useQuery } from '@tanstack/react-query';
import {
  useSharedValue,
  withSpring,
} from 'react-native-reanimated';
import SwipeableRobot, { RobotHandle } from '@components/RobotHelper/SwipeableRobot';
import { switchCompany, cancelAllRequests } from '@api/api';
import { SHOW_AUTH_MODAL } from "@reduxLocal/actions/types";
import AvatarHandler from "@components/Global/AvatarHandler/AvatarHandler";
import ErrorBoundary from "@utils/ErrorBoundary";
import { selectUserAvatar, selectUserProfile } from '@reduxLocal/selectors/userSelectors';
import { store } from '@reduxLocal/persistState';
import { useCanAccessPremiumFeatures, useEnhancedSubscriptionStatus } from '@hooks/useSubscriptionStatus';
import {
  Menu,
  MenuItem,
  MenuList,
  Button,
  Avatar,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  Box,
  Chip,
  IconButton,
  Paper,
  styled as muiStyled
} from '@mui/material';
import { styled, Stack, XStack } from 'tamagui';
import { Title } from '@constants/Themed';

// Hover Label component for tooltips (similar to statusBar.tsx)
const HoverLabel = ({ label, children, position = 'bottom' }: { label: string; children: React.ReactNode; position?: 'top' | 'bottom' | 'left' | 'right' }) => {
  const [isVisible, setIsVisible] = useState(false);

  const positionStyles = {
    top: { bottom: '100%', left: '50%', transform: 'translateX(-50%)', marginBottom: 12 },
    bottom: { top: '100%', left: '50%', transform: 'translateX(-50%)', marginTop: 12 },
    left: { right: '100%', top: '50%', transform: 'translateY(-50%)', marginRight: 12 },
    right: { left: '100%', top: '50%', transform: 'translateY(-50%)', marginLeft: 12 },
  };

  if (Platform.OS !== 'web') {
    return <>{children}</>;
  }

  return (
    <View style={{ position: 'relative' }}>
      <Pressable
        onHoverIn={() => setIsVisible(true)}
        onHoverOut={() => setIsVisible(false)}
        style={{ position: 'relative' }}
      >
        {children}
        {isVisible && (
          <View style={[{
            position: 'absolute',
            backgroundColor: 'rgba(0, 0, 0, 0.85)',
            paddingHorizontal: 10,
            paddingVertical: 6,
            borderRadius: 6,
            zIndex: 1000,
            minWidth: 'max-content',
            whiteSpace: 'nowrap',
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 2 },
            shadowOpacity: 0.25,
            shadowRadius: 4,
            elevation: 5,
            ...positionStyles[position]
          } as any]}>
            <Title style={{ color: "white", fontSize: 13, fontWeight: '600' }}>
              {label}
            </Title>
            {/* Triangle arrow */}
            <View style={[{
              position: 'absolute',
              width: 0,
              height: 0,
              borderLeftWidth: 6,
              borderRightWidth: 6,
              borderLeftColor: 'transparent',
              borderRightColor: 'transparent',
              left: '50%',
              transform: 'translateX(-50%)',
              ...(position === 'bottom' ? {
                top: -6,
                borderBottomWidth: 6,
                borderBottomColor: 'rgba(0, 0, 0, 0.85)'
              } : {
                bottom: -6,
                borderTopWidth: 6,
                borderTopColor: 'rgba(0, 0, 0, 0.85)'
              })
            } as any]} />
          </View>
        )}
      </Pressable>
    </View>
  );
};

// Styled components for better Material UI integration
const StyledMenuPaper = muiStyled(Paper, {
  shouldForwardProp: (prop) => prop !== 'isLightTheme',
})<{ isLightTheme?: boolean }>(({ isLightTheme }) => ({
  minWidth: 320,
  maxWidth: 420,
  maxHeight: '70vh',
  overflow: 'hidden',
  borderRadius: 16,
  backgroundColor: isLightTheme ? '#ffffff' : '#1e293b',
  boxShadow: isLightTheme
    ? '0 8px 32px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.08)'
    : '0 8px 32px rgba(0,0,0,0.4), 0 2px 8px rgba(0,0,0,0.3)',
  border: isLightTheme ? '1px solid #e2e8f0' : '1px solid #334155',
}));

// Tamagui styled MenuItem replacement
const StyledMenuItemBase = styled(Stack, {
  name: 'StyledMenuItem',
  padding: 12,
  paddingLeft: 16,
  paddingRight: 16,
  minHeight: 64,
  borderRadius: 8,
  marginHorizontal: 8,
  marginVertical: 4,
  flexDirection: 'row',
  alignItems: 'center',
  cursor: 'pointer',
  tag: 'div',
  backgroundColor: 'transparent',
} as const);

// Wrapper component to handle onClick
const StyledMenuItem: React.FC<{
  onClick?: () => void;
  selected?: boolean;
  isLightTheme?: boolean;
  disabled?: boolean;
  children: React.ReactNode;
}> = ({ onClick, selected, isLightTheme = true, disabled = false, children }) => {
  // Calculate background color based on theme and selection
  const getBackgroundColor = () => {
    if (disabled) {
      return isLightTheme ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.04)';
    }
    if (selected) {
      return isLightTheme ? 'rgba(59,130,246,0.12)' : 'rgba(96,165,250,0.16)';
    }
    return 'transparent';
  };

  const getHoverBackgroundColor = () => {
    if (disabled) {
      return getBackgroundColor();
    }
    if (selected) {
      return isLightTheme ? 'rgba(59,130,246,0.16)' : 'rgba(96,165,250,0.2)';
    }
    return isLightTheme ? 'rgba(59,130,246,0.08)' : 'rgba(96,165,250,0.12)';
  };

  return (
    <div
      style={{
        backgroundColor: getBackgroundColor(),
        padding: 12,
        paddingLeft: 16,
        paddingRight: 16,
        minHeight: 64,
        borderRadius: 8,
        marginLeft: 8,
        marginRight: 8,
        marginTop: 4,
        marginBottom: 4,
        display: 'flex',
        alignItems: 'center',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        pointerEvents: disabled ? 'none' : 'auto',
      }}
      onClick={disabled ? undefined : onClick}
    >
      {children}
    </div>
  );
};

const CompanyTriggerButton = muiStyled(Button, {
  shouldForwardProp: (prop) => prop !== 'isLightTheme',
})<{ isLightTheme?: boolean }>(({ isLightTheme }) => ({
  borderRadius: 12,
  padding: '10px 14px',
  minHeight: 'auto',
  height: 'auto',
  minWidth: 'auto',
  width: 'auto',
  textTransform: 'none',
  position: 'relative',
  overflow: 'hidden',
  background: 'linear-gradient(135deg, #C8A2C8 0%, #87CEEB 100%)',
  color: isLightTheme ? '#1e293b' : '#f1f5f9',
  boxShadow: isLightTheme
    ? '0 2px 4px rgba(0,0,0,0.1)'
    : '0 2px 4px rgba(0,0,0,0.4)',
  border: isLightTheme ? '1px solid rgba(200, 162, 200, 0.3)' : '1px solid rgba(135, 206, 235, 0.3)',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: isLightTheme ? 'rgba(255, 255, 255, 0.85)' : 'rgba(0, 0, 0, 0.75)',
    backdropFilter: 'blur(20px)',
    zIndex: 0,
  },
  '& > *': {
    position: 'relative',
    zIndex: 1,
  },
  '&:hover': {
    '&::before': {
      backgroundColor: isLightTheme ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.8)',
    },
    boxShadow: isLightTheme
      ? '0 4px 8px rgba(200, 162, 200, 0.3)'
      : '0 4px 8px rgba(135, 206, 235, 0.3)',
  },
  '&.MuiButton-root': {
    fontSize: '14px',
    fontWeight: 600,
    lineHeight: 1.4,
    whiteSpace: 'nowrap',
  }
}));


// Helper function to check if a company subscription is valid and selectable
const isCompanySubscriptionValid = (company: any): boolean => {
  // Free accounts (no billing_info) are always valid
  if (!company?.billing_info) {
    return true;
  }

  // Extract active billing info from company's billing_info object
  const billingInfo = Object.values(company.billing_info || {}).find((info: any) => info?.isActive);

  // No active billing info means free account, which is valid
  if (!billingInfo) {
    return true;
  }

  const status = billingInfo.status || 'inactive';
  const endDate = billingInfo.endDate ? new Date(billingInfo.endDate) : null;
  const isExpired = endDate && endDate < new Date();

  // Expired subscriptions are invalid
  if (isExpired) {
    return false;
  }

  // Invalid statuses that prevent selection
  const invalidStatuses = ['cancelled', 'past_due', 'incomplete_expired'];
  if (invalidStatuses.includes(status)) {
    return false;
  }

  // Valid statuses: active, subscribed, trialing, incomplete (not expired)
  return true;
};

// Helper function to get subscription status from company billing_info
// billing_info is now included in the company list response from the backend
const getCompanySubscriptionStatus = (company: any) => {
  if (!company?.billing_info) {
    return { status: 'free', label: 'Free', color: 'default', isValid: true };
  }

  // Extract active billing info from company's billing_info object
  const billingInfo = Object.values(company.billing_info || {}).find((info: any) => info?.isActive);

  if (!billingInfo) {
    return { status: 'free', label: 'Free', color: 'default', isValid: true };
  }

  const status = billingInfo.status || 'inactive';
  const endDate = billingInfo.endDate ? new Date(billingInfo.endDate) : null;
  const isExpired = endDate && endDate < new Date();

  // Determine status label and color
  if (isExpired) {
    return { status: 'expired', label: 'Expired', color: 'error', isValid: false };
  }

  switch (status) {
    case 'active':
    case 'subscribed':
    case 'trialing':
      return {
        status: 'active',
        label: status === 'trialing' ? 'Trial' : 'Premium',
        color: status === 'trialing' ? 'warning' : 'success',
        isValid: true
      };
    case 'cancelled':
      return { status: 'cancelled', label: 'Cancelled', color: 'error', isValid: false };
    case 'past_due':
      return { status: 'past_due', label: 'Past Due', color: 'error', isValid: false };
    case 'incomplete':
      return { status: 'incomplete', label: 'Incomplete', color: 'warning', isValid: true };
    case 'incomplete_expired':
      return { status: 'incomplete_expired', label: 'Incomplete Expired', color: 'error', isValid: false };
    default:
      return { status: 'inactive', label: 'Inactive', color: 'default', isValid: true };
  }
};

const CompanySelector = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectCompanyOpen, setSelectCompanyOpen] = useState(false);
  const theme = themeSelector();
  const dispatch = useDispatch();
  const currentCompany = useSelector((state: DefaultRootState) => state.app.selectedProfile);
  const navigation = useNavigation();
  const queryClient = useQueryClient();
  const subState = queryClient.getQueryState(['subscriptionPlan']);
  const subscriptionPlan: { product: string, plan: any } = queryClient.getQueryData(['subscriptionPlan']);
  const robotRef = useRef<RobotHandle>(null);
  const avatar = useSelector(selectUserAvatar);
  const userDetails = useSelector(selectUserProfile);
  // Get target user info if we're in target user context
  const targetUser = useSelector((state: DefaultRootState) => state.promiseData.data?.userData?.targetUser);
  const targetUserId = useSelector((state: DefaultRootState) => state.promiseData.data?.userData?.targetUserId);
  const isTargetUserContext = !!targetUserId && !currentCompany;
  // Check if we're in personal account mode (no company, no target user)
  // Explicitly check for null/undefined/empty string to ensure proper highlighting
  const isPersonalAccount = (currentCompany === null || currentCompany === undefined) &&
    (targetUserId === null || targetUserId === undefined || targetUserId === '');
  const [xyValue, setXYValue] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [help, setHelp] = useState(false);
  const [companyList, setCompanyList] = useState([]);
  const [userList, setUserList] = useState([]);
  const addCompanyButtonRef = useRef(null);

  // Check if user has premium access for user switching feature
  const canAccessPremiumFeatures = useCanAccessPremiumFeatures();
  const currentUserId = useSelector((state: DefaultRootState) => state.promiseData.data?.userData?.id);
  const originalUserId = useSelector((state: DefaultRootState) => state.promiseData.data?.userData?.originalUserId);
  // Get original user profile info for display
  const originalUserProfile = useSelector((state: DefaultRootState) => state.promiseData.data?.userData);

  // Subscription status hooks
  const subscription = useEnhancedSubscriptionStatus();
  const user = useSelector((state: DefaultRootState) => state?.promiseData?.data?.userData);

  // Normalize plan data same way as PlanComponent does
  const normalizedPlan = useMemo(() => {
    if (!subscriptionPlan) {
      return null;
    }

    const currentPlanAny = subscriptionPlan as any;

    const nestedData = currentPlanAny?.data?.data ?? currentPlanAny?.data;
    if (Array.isArray(nestedData) && nestedData[0]) {
      return nestedData[0];
    }

    return currentPlanAny;
  }, [subscriptionPlan]);

  // Get plan name using same logic as PlanComponent
  const planName = useMemo(() => {
    if (!subscriptionPlan) return null;

    const currentPlanAny = subscriptionPlan as any;
    // Same order as PlanComponent: product (string) -> product.name -> normalizedPlan.plan.product
    const name = currentPlanAny?.product ||
      currentPlanAny?.product?.name ||
      normalizedPlan?.plan?.product ||
      subscription.productName; // Fallback to enhanced subscription hook

    // Don't show product IDs (starting with 'prod_')
    if (name && typeof name === 'string' && name.startsWith('prod_')) {
      return null; // Will fallback to "Free"
    }

    // Handle EXEMPT_PRODUCT
    if (name === 'EXEMPT_PRODUCT') {
      return 'SmartSaaS';
    }

    return name;
  }, [subscriptionPlan, normalizedPlan, subscription.productName]);

  // Check if user is premium (similar to StaticMenuHeader)
  const isPremiumUser = useCallback(() => {
    if (!user) return false;

    // Check multiple premium indicators with safe access
    const isPremium = user?.isPremium === true;
    const hasActiveSubscription = user?.subscriptionStatus === 'active';
    const hasBillingActive = user?.billing?.isActive === true;

    return isPremium || hasActiveSubscription || hasBillingActive;
  }, [
    String(user?.id || ''),
    Boolean(user?.isPremium),
    String(user?.subscriptionStatus || ''),
    Boolean(user?.billing?.isActive)
  ]);

  // Check if user is in trial mode (similar to StaticMenuHeader and PlanComponent)
  const isInTrial = useMemo(() => {
    if (!normalizedPlan && !subscription.subscriptionPlan) return false;

    // Use normalizedPlan first (same as PlanComponent)
    const subscriptionData = normalizedPlan ||
      subscription.subscriptionPlan?.data?.data?.[0] ||
      subscription.subscriptionPlan?.data?.[0] ||
      subscription.subscriptionPlan;

    // Check explicit trialing status
    if (subscriptionData?.status === 'trialing') return true;

    // Also check if trial_end exists and is in the future
    if (subscriptionData?.trial_end) {
      const trialEndDate = new Date(subscriptionData.trial_end * 1000);
      const now = new Date();
      if (trialEndDate > now) {
        return true; // Still in trial period
      }
    }

    return false;
  }, [
    normalizedPlan?.status,
    normalizedPlan?.trial_end,
    subscription.subscriptionPlan?.data?.data?.[0]?.status,
    subscription.subscriptionPlan?.data?.data?.[0]?.trial_end,
    subscription.subscriptionPlan?.data?.[0]?.status,
    subscription.subscriptionPlan?.data?.[0]?.trial_end,
    subscription.subscriptionPlan?.status,
    subscription.subscriptionPlan?.trial_end,
  ]);

  // Material UI menu handlers
  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    setSelectCompanyOpen(true);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectCompanyOpen(false);
  };

  // Company switching handler
  const handleCompanySwitch = async (companyId: string) => {
    const company = companyList.find(c => c._id === companyId);

    // Check if this is a "personal" account (connected user) - should be treated as user switch
    if (company?.isConnectedUser) {
      // This is a connected user marked as "personal", switch to user instead of company
      const targetUserId = company._id || company.linkedId;
      if (!targetUserId) {
        dispatch({
          type: SHOW_AUTH_MODAL,
          data: {
            content: "Invalid user account",
            type: 'error'
          }
        });
        return;
      }

      // Check if TARGET user has premium subscription (required for user switching)
      // billing_info is included in the company list response for connected users
      const targetUserBillingInfo = Object.values(company.billing_info || {}).find((info: any) => info?.isActive);
      const targetUserHasPremium = targetUserBillingInfo &&
        (targetUserBillingInfo.status === 'active' || targetUserBillingInfo.status === 'subscribed' || targetUserBillingInfo.status === 'trialing') &&
        new Date(targetUserBillingInfo.endDate || 0) > new Date();

      if (!targetUserHasPremium) {
        dispatch({
          type: SHOW_AUTH_MODAL,
          data: {
            content: "Cannot switch to this user. The target user must have an active premium subscription.",
            type: 'error'
          }
        });
        return;
      }

      handleMenuClose();
      await cancelAllRequests().then(async () => {
        try {
          await switchCompany({ targetUserId }).then(() => {
            navigation.navigate('LoadingScreen' as never);
            // Clear company context - user switching is personal account mode
            dispatch({ type: CLEAR_COMPANY });
            // Dismiss payment banner when switching users
            dispatch({ type: HIDE_PAYMENT_REQUIRED });
            // Invalidate all queries when switching users
            queryClient.invalidateQueries({ queryKey: ['subscriptionPlan'] });
            queryClient.invalidateQueries({ queryKey: ['all-users'] });

            queryClient.invalidateQueries({ predicate: (query) => query.queryKey[0] === 'subscriptionPlan' });
            queryClient.invalidateQueries({ queryKey: ['billingInfo'] });
            queryClient.invalidateQueries({ predicate: (query) => query.queryKey[0] === 'billingInfo' });
            queryClient.invalidateQueries({ queryKey: ['planData'] });
            queryClient.invalidateQueries({ queryKey: ['planSubscriptions'] });
            queryClient.invalidateQueries({ queryKey: ['companyList'] });
            queryClient.invalidateQueries({ queryKey: ['data-folders'] });
            queryClient.invalidateQueries({ queryKey: ['dataFolders'] });
            queryClient.invalidateQueries({ queryKey: ['dataFoldersPaginated'] });
            queryClient.resetQueries();
            // Remove all other queries except companyList
            queryClient.removeQueries({
              predicate: (query) => query.queryKey[0] !== 'companyList'
            });
          });
        } catch (err) {
          dispatch({ type: SHOW_AUTH_MODAL, data: { content: "Error switching user", details: err } });
        }
      });
      return;
    }

    // Regular company switch (not a connected user)
    // Check if company subscription is valid before switching
    if (company && !isCompanySubscriptionValid(company)) {
      const subscriptionStatus = getCompanySubscriptionStatus(company);
      dispatch({
        type: SHOW_AUTH_MODAL,
        data: {
          content: `Cannot switch to this company. Subscription status: ${subscriptionStatus.label}`,
          type: 'error'
        }
      });
      return;
    }

    handleMenuClose();
    await cancelAllRequests().then(async () => {
      await switchCompany({ companyNumber: companyId }).then(() => {
        navigation.navigate('LoadingScreen' as never);
        // Dismiss payment banner when company switches
        // Payment limits are company-specific, so banner should be hidden until new company's limits are checked
        dispatch({ type: HIDE_PAYMENT_REQUIRED });
        dispatch({
          type: SWITCH_COMPANY_REQUEST,
          payload: {
            id: companyId,
            logo: company?.logo,
            companyName: company?.company_name
          }
        });
        // Invalidate all billing-related queries when company switches
        // This ensures billing data is refetched for the new company context
        queryClient.invalidateQueries({ queryKey: ['subscriptionPlan'] });
        queryClient.invalidateQueries({ predicate: (query) => query.queryKey[0] === 'subscriptionPlan' });
        queryClient.invalidateQueries({ queryKey: ['billingInfo'] });
        queryClient.invalidateQueries({ predicate: (query) => query.queryKey[0] === 'billingInfo' });
        queryClient.invalidateQueries({ queryKey: ['planData'] });
        queryClient.invalidateQueries({ queryKey: ['planSubscriptions'] });
        // Refetch company list to get updated subscription statuses
        queryClient.invalidateQueries({ queryKey: ['companyList'] });
        // Invalidate data folder queries - these rely on session companyNumber
        queryClient.invalidateQueries({ queryKey: ['data-folders'] });
        queryClient.invalidateQueries({ queryKey: ['dataFolders'] });
        queryClient.invalidateQueries({ queryKey: ['dataFoldersPaginated'] });
        // Remove all other queries except companyList
        queryClient.removeQueries({
          predicate: (query) => query.queryKey[0] !== 'companyList'
        });
      }).catch((err) => {
        dispatch({ type: SHOW_AUTH_MODAL, data: { content: "Error switching company", details: err } });
      });
    });
  };

  // Personal account handler
  const handlePersonalAccount = async () => {
    handleMenuClose();
    await abortAllRequests();
    // Explicitly clear both companyNumber and targetUserId when switching to personal account
    switchCompany({ companyNumber: null, targetUserId: null }).then(() => {
      // Dismiss payment banner when switching to personal account
      // Payment limits are account-specific, so banner should be hidden until personal account limits are checked
      dispatch({ type: HIDE_PAYMENT_REQUIRED });
      dispatch({ type: CLEAR_COMPANY });
      navigation.navigate('LoadingScreen' as never);
      // Invalidate all billing-related queries when switching to personal account
      // This ensures billing data is refetched for personal account context
      queryClient.invalidateQueries({ queryKey: ['subscriptionPlan'] });
      queryClient.invalidateQueries({ predicate: (query) => query.queryKey[0] === 'subscriptionPlan' });
      queryClient.invalidateQueries({ queryKey: ['billingInfo'] });
      queryClient.invalidateQueries({ predicate: (query) => query.queryKey[0] === 'billingInfo' });
      queryClient.invalidateQueries({ queryKey: ['planData'] });
      queryClient.invalidateQueries({ queryKey: ['planSubscriptions'] });
      // Remove all other queries except companyList
      queryClient.removeQueries({
        predicate: (query) => query.queryKey[0] !== 'companyList'
      });
    });
  };

  // User switching handler (premium feature)
  const handleUserSwitch = async (targetUserId: string) => {
    handleMenuClose();

    // If switching to original user, treat as switching to personal account (clear targetUserId)
    const effectiveOriginalUserId = originalUserId || currentUserId;
    if (targetUserId === effectiveOriginalUserId) {
      // Switch back to personal account
      await cancelAllRequests().then(async () => {
        try {
          await switchCompany({ targetUserId: null }).then(() => {
            navigation.navigate('LoadingScreen' as never);
            // Clear company context in Redux
            dispatch({ type: CLEAR_COMPANY });
            // Dismiss payment banner
            dispatch({ type: HIDE_PAYMENT_REQUIRED });
            // Invalidate all queries
            queryClient.invalidateQueries({ queryKey: ['subscriptionPlan'] });
            queryClient.invalidateQueries({ predicate: (query) => query.queryKey[0] === 'subscriptionPlan' });
            queryClient.invalidateQueries({ queryKey: ['billingInfo'] });
            queryClient.invalidateQueries({ predicate: (query) => query.queryKey[0] === 'billingInfo' });
            queryClient.invalidateQueries({ queryKey: ['planData'] });
            queryClient.invalidateQueries({ queryKey: ['planSubscriptions'] });
            queryClient.invalidateQueries({ queryKey: ['companyList'] });
            queryClient.invalidateQueries({ queryKey: ['data-folders'] });
            queryClient.invalidateQueries({ queryKey: ['dataFolders'] });
            queryClient.invalidateQueries({ queryKey: ['dataFoldersPaginated'] });
            queryClient.removeQueries({
              predicate: (query) => query.queryKey[0] !== 'companyList'
            });
          });
        } catch (err) {
          dispatch({ type: SHOW_AUTH_MODAL, data: { content: "Error switching to personal account", details: err } });
        }
      });
      return;
    }

    // Switch to another user
    await cancelAllRequests().then(async () => {
      try {
        await switchCompany({ targetUserId }).then(() => {
          navigation.navigate('LoadingScreen' as never);
          // Clear company context in Redux - user switching is personal account mode
          dispatch({ type: CLEAR_COMPANY });
          // Dismiss payment banner when switching users
          dispatch({ type: HIDE_PAYMENT_REQUIRED });
          // Invalidate all queries when switching users
          queryClient.invalidateQueries({ queryKey: ['subscriptionPlan'] });
          queryClient.invalidateQueries({ predicate: (query) => query.queryKey[0] === 'subscriptionPlan' });
          queryClient.invalidateQueries({ queryKey: ['billingInfo'] });
          queryClient.invalidateQueries({ predicate: (query) => query.queryKey[0] === 'billingInfo' });
          queryClient.invalidateQueries({ queryKey: ['planData'] });
          queryClient.invalidateQueries({ queryKey: ['planSubscriptions'] });
          queryClient.invalidateQueries({ queryKey: ['companyList'] });
          queryClient.invalidateQueries({ queryKey: ['data-folders'] });
          queryClient.invalidateQueries({ queryKey: ['dataFolders'] });
          queryClient.invalidateQueries({ queryKey: ['dataFoldersPaginated'] });
          // Remove all other queries except companyList
          queryClient.removeQueries({
            predicate: (query) => query.queryKey[0] !== 'companyList'
          });
        });
      } catch (err) {
        dispatch({ type: SHOW_AUTH_MODAL, data: { content: "Error switching user", details: err } });
      }
    });
  };

  // Detect active context from session on initial render and when companyList loads
  useEffect(() => {
    const initializeFromSession = async () => {
      try {
        dispatch({ type: SHOW_LOADING_MODAL, data: { content: 'Detecting session...' } });
        const session = await getSession();
        if (session?.data) {
          // PRIORITY: companyNumber > targetUserId > personal account
          if (session.data.company) {
            const item = session.data.company;
            // Wait for companyList to load if not available yet
            if (companyList.length === 0) {
              dispatch({ type: HIDE_LOADING_MODAL });
              return; // Will retry when companyList loads
            }
            // Find matching company in companyList
            const matchingCompany = companyList.find(c => c._id === item._id);
            if (matchingCompany) {
              // Only update if not already set or if different
              if (!currentCompany || currentCompany.id !== item._id) {
                dispatch({
                  type: SWITCH_COMPANY_REQUEST,
                  payload: {
                    id: item._id,
                    logo: item.logo || matchingCompany.logo,
                    companyName: item.company_name || item.copmany_name || matchingCompany.company_name
                  }
                });
              }
            }
          } else if (session.data.targetUser) {
            // Target user context - clear company selection
            // Note: targetUser is not shown in company list, it's in user list
            if (currentCompany) {
              dispatch({ type: CLEAR_COMPANY });
            }
          } else {
            // Personal account - clear company selection
            if (currentCompany) {
              dispatch({ type: CLEAR_COMPANY });
            }
          }
        }
      } catch (error) {
        console.warn('Failed to initialize from session:', error);
      } finally {
        dispatch({ type: HIDE_LOADING_MODAL });
      }
    };

    // Run on mount and when companyList changes
    initializeFromSession();
  }, [companyList, currentCompany]); // Run when companyList or currentCompany changes

  useEffect(() => {
    const unsubscribe = store.subscribe(() => {
      const action = store.getState().lastAction;
      if (action && action.type === "SHOW_COMPANY_SELECT") {
        setSelectCompanyOpen(true);
        setTimeout(() => {
          addCompanyButtonRef.current?.bounce()
        }, 500)
      }
    })
    return () => unsubscribe()
  }, [])

  useEffect(() => {
    if (subState?.status == 'pending') {
      //fetch query

    }

  }, [subscriptionPlan, subState]);

  const handleShowHelp = useCallback(() => {
    setCurrentIndex(currentIndex + 1 > textHelp.length - 1 ? 0 : currentIndex + 1);
    setHelp(true);
  }, [currentIndex, textHelp.length])


  const getText = () => {
    return textHelp[currentIndex];
  }

  // Fetch company list with subscription statuses (billing_info included from backend)
  // Refetch when subscription queries are invalidated to get updated billing_info
  useQuery({
    queryKey: ['companyList'],
    queryFn: async () => {
      const data = await getCompanyList().then(res => {
        setCompanyList(res)
        return res
      });
      return data;
    },
    retry: 3,
    staleTime: 10000,
    refetchOnWindowFocus: true,
  });

  // Fetch user list for premium user switching (only if premium)
  // Note: This should only return users who have allowed/invited the current user to share data
  // We get connectedUsers from getCompanyList, which already filters by canSwitchContext !== false
  useQuery({
    queryKey: ['userList', currentUserId],
    queryFn: async () => {
      if (!canAccessPremiumFeatures) {
        return [];
      }
      try {
        // Get company list which includes connectedUsers (users who have shared data)
        // The backend already filters to only include users where canSwitchContext !== false
        // Try to use cached data first, otherwise fetch
        let companyListData = queryClient.getQueryData(['companyList']);
        if (!companyListData) {
          companyListData = await getCompanyList();
        }
        const effectiveOriginalUserId = originalUserId || currentUserId;
        
        // Extract connectedUsers from company list (entries with isConnectedUser: true)
        // These are users who have allowed the current user to switch to their context
        // The backend already filters to only include users where canSwitchContext !== false
        // getCompanyList returns the data array directly
        const connectedUsers = (Array.isArray(companyListData) ? companyListData : []).filter((item: any) => {
          return item?.isConnectedUser === true && item?.linkedId;
        });

        // Transform connectedUsers to user format for the user switching section
        const users = connectedUsers.map((connectedUser: any) => {
          const userId = connectedUser.linkedId || connectedUser._id;
          // Extract user info from the connected user entry
          // The company_name field contains the user's full name (name + surname)
          const fullName = (connectedUser.company_name || '').trim();
          const nameParts = fullName.split(/\s+/);
          const name = nameParts[0] || '';
          const surname = nameParts.slice(1).join(' ') || '';
          
          // Avatar is stored in logo field as image URL string
          const avatarImg = connectedUser.logo || null;
          
          return {
            _id: userId,
            id: userId,
            email: connectedUser.company_tagline || '', // company_tagline contains email for connected users
            name: name,
            surname: surname,
            avatar: avatarImg ? { img: avatarImg } : null, // Convert to object format expected by AvatarHandler
            status: 'active',
            isOriginalUser: userId === effectiveOriginalUserId
          };
        });

        // Add original user if not already in the list
        const originalUserInList = users.find(u => u._id === effectiveOriginalUserId);
        if (!originalUserInList && originalUserProfile) {
          users.unshift({
            _id: effectiveOriginalUserId,
            id: effectiveOriginalUserId,
            email: originalUserProfile.email,
            name: originalUserProfile.profile_information?.name,
            surname: originalUserProfile.profile_information?.surname,
            avatar: originalUserProfile.profile_information?.avatar,
            status: 'active',
            isOriginalUser: true
          });
        }

        setUserList(users);
        return users;
      } catch (error) {
        console.error('Error fetching user list:', error);
        return [];
      }
    },
    enabled: canAccessPremiumFeatures && !!currentUserId,
    retry: 1,
    staleTime: 30000, // 30 seconds
    refetchOnWindowFocus: false,
  });

  // Refetch company list when subscription queries are invalidated to get updated subscription statuses
  useEffect(() => {
    const subscription = queryClient.getQueryCache().subscribe((event) => {
      if (
        event?.type === 'updated' &&
        (event.query.queryKey[0] === 'subscriptionPlan' ||
          event.query.queryKey[0] === 'billingInfo' ||
          event.query.queryKey[0] === 'planSubscriptions')
      ) {
        // Refetch company list to get updated subscription statuses
        queryClient.invalidateQueries({ queryKey: ['companyList'] });
      }
    });

    return () => {
      subscription();
    };
  }, [queryClient]);
  return (
    <ErrorBoundary >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} id="CompanySelector">
        {/* Company Selector Trigger Button */}
        <HoverLabel label="Switch Account or Company">
          <CompanyTriggerButton
            onClick={handleMenuClick}
            endIcon={<FontAwesome name="chevron-down" size={12} color={theme ? '#1e293b' : '#f1f5f9'} />}
            isLightTheme={theme}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {currentCompany?.logo ? (
                <Avatar
                  src={currentCompany.logo}
                  sx={{
                    borderRadius: 0,
                    width: 24,
                    height: 24,
                    mr: 1,
                    '& img': {
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      borderRadius: 0
                    }
                  }}
                />
              ) : targetUser?.avatar ? (
                <Avatar
                  src={targetUser.avatar}
                  sx={{
                    borderRadius: '50%',
                    width: 24,
                    height: 24,
                    mr: 1,
                    '& img': {
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      borderRadius: '50%'
                    }
                  }}
                />
              ) : (
                <FontAwesome
                  name="user-circle"
                  size={24}
                  color={theme ? "#1e293b" : "#f1f5f9"}
                  style={{ marginRight: 8 }}
                />
              )}
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {currentCompany?.companyName ||
                  (isTargetUserContext && targetUser ?
                    `${targetUser.name || ''} ${targetUser.surname || ''}`.trim() || targetUser.email :
                    (planName || (subState?.status !== 'pending' && "Free")))}
              </Typography>
            </Box>
          </CompanyTriggerButton>
        </HoverLabel>


        {/* Material UI Menu */}
        <Menu
          className="hide-scrollbar-virtuoso"
          anchorEl={anchorEl}
          open={selectCompanyOpen}
          onClose={handleMenuClose}
          PaperProps={{
            className: 'hide-scrollbar-virtuoso',
            style: {
              backgroundColor: theme ? "rgba(255,255,255,0.95)" : "rgba(30,41,59,0.95)",
              borderRadius: 12,
              minWidth: 200,
              padding: 8,
              backdropFilter: Platform.OS === 'web' ? 'blur(24px)' : undefined,
              boxShadow: Platform.OS === 'web'
                ? (theme
                  ? '0 12px 48px rgba(0,0,0,0.12), 0 4px 16px rgba(0,0,0,0.08)'
                  : '0 12px 48px rgba(255,255,255,0.08), 0 4px 16px rgba(255,255,255,0.04)'
                ) : undefined,
            }
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          sx={{
            '& .MuiMenu-paper': {
              mt: 1,
              ml: 0,
              backgroundColor: theme ? '#ffffff' : '#1e293b',
            },
            '& .MuiBackdrop-root': {
              backgroundColor: theme ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.7)',
            }
          }}
        >
          <MenuList className="hide-scrollbar-virtuoso">
            {/* Menu Header */}
            <XStack>
            <Box sx={{
              p: 2,
              borderBottom: `1px solid ${theme ? '#e2e8f0' : '#334155'}`,
              backgroundColor: theme ? '#f8fafc' : '#0f172a'
            }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: theme ? '#1e293b' : '#f1f5f9' }}>
                Your Accounts
              </Typography>
              <Typography variant="body2" sx={{ color: theme ? '#64748b' : '#94a3b8', fontStyle: 'italic', mt: 0.5 }}>
                Any new company accounts will be billed separately
              </Typography>
            </Box>
              {/* Add Company Button */}
              <Box sx={{
                p: 2,
                backgroundColor: theme ? '#f8fafc' : '#0f172a',
                borderTop: `1px solid ${theme ? '#e2e8f0' : '#334155'}`
              }}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => {
                    dispatch({ type: SHOW_ADD_COMPANY_MODAL });
                    handleMenuClose();
                  }}
                  startIcon={<FontAwesome name="plus" size={16} color="white" />}
                  sx={{
                    backgroundColor: 'rgb(90,192,234)',
                    borderRadius: 2,
                    py: 1.5,
                    fontWeight: 700,
                    textTransform: 'none',
                    boxShadow: '0 4px 8px rgba(90,192,234,0.3)',
                    '&:hover': {
                      backgroundColor: 'rgb(80,172,214)',
                      boxShadow: '0 6px 12px rgba(90,192,234,0.4)',
                    }
                  }}
                >
                  Add Company
                </Button>
              </Box>
            </XStack>
            {/* Personal Account - Highlighted when no company and no target user */}
            <StyledMenuItem
              onClick={handlePersonalAccount}
              selected={isPersonalAccount}
              isLightTheme={theme}
            >
              <ListItemIcon>
                <Box sx={{ width: 40, height: 40, borderRadius: '10px', overflow: 'hidden', mr: 1 }}>
                  <AvatarHandler
                    avatar={{ img: avatar?.img }}
                    avatarImageStyle={{
                      width: 40,
                      height: 40,
                      left: -10,
                      top: -10,
                      borderRadius: 10
                    }}
                  />
                </Box>
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography variant="body1" sx={{ fontWeight: 600, color: theme ? '#1e293b' : '#f1f5f9' }}>
                    {toTitleCase(userDetails?.profile_information?.name || '')} {toTitleCase(userDetails?.profile_information?.surname || '')}
                  </Typography>
                }
                secondary={
                  <Typography variant="body2" sx={{ color: theme ? '#64748b' : '#94a3b8' }}>
                    {userDetails?.email}
                  </Typography>
                }
              />
              {!currentCompany && (
                <Chip
                  label="Current"
                  size="small"
                  sx={{
                    backgroundColor: 'rgba(90,192,234,0.1)',
                    color: 'rgb(90,192,234)',
                    fontWeight: 600
                  }}
                />
              )}
            </StyledMenuItem>

            <Divider sx={{
              mx: 1,
              borderColor: theme ? '#e2e8f0' : '#334155'
            }} />

            {/* Company List Sections */}
            {companyList.length > 0 && (
              <>
                {/* Awaiting Completion */}
                {companyList.filter(item => item?.pendingApproval).length > 0 && (
                  <>
                    <Box sx={{
                      px: 2,
                      py: 1,
                      backgroundColor: theme ? '#f1f5f9' : '#0f172a'
                    }}>
                      <Typography variant="caption" sx={{
                        fontWeight: 700,
                        color: theme ? '#475569' : '#cbd5e1',
                        textTransform: 'uppercase',
                        letterSpacing: 1
                      }}>
                        Awaiting Completion
                      </Typography>
                    </Box>
                    {companyList.filter(item => item?.pendingApproval).map((company) => {
                      const isValid = isCompanySubscriptionValid(company);
                      return (
                        <StyledMenuItem
                          key={company._id}
                          onClick={() => handleCompanySwitch(company._id)}
                          selected={currentCompany?.id === company._id}
                          isLightTheme={theme}
                          disabled={!isValid}
                        >
                      <ListItemIcon>
                        <Avatar
                          src={company.logo}
                          sx={{
                            width: 40,
                            height: 40,
                            mr: 1,
                            borderRadius: 0,
                            '& img': {
                              width: '100%',
                              height: '100%',
                              objectFit: 'contain',
                              borderRadius: 0
                            }
                          }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="body1" sx={{ fontWeight: 600, color: theme ? '#1e293b' : '#f1f5f9' }}>
                            {toTitleCase(company.company_name)}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="body2" sx={{ color: theme ? '#64748b' : '#94a3b8' }}>
                            {company.company_tagline}
                          </Typography>
                        }
                      />
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <Chip
                              label="Pending"
                              size="small"
                              color="warning"
                              variant="outlined"
                            />
                            {(() => {
                              const subscriptionStatus = getCompanySubscriptionStatus(company);
                              return (
                                <Chip
                                  label={subscriptionStatus.label}
                                  size="small"
                                  color={subscriptionStatus.color as any}
                                  variant={subscriptionStatus.status === 'active' ? 'filled' : 'outlined'}
                                  sx={{
                                    fontWeight: 600,
                                    fontSize: '10px',
                                    ...(subscriptionStatus.color === 'success' && {
                                      backgroundColor: 'rgba(34,197,94,0.15)',
                                      color: 'rgb(34,197,94)',
                                      borderColor: 'rgba(34,197,94,0.3)'
                                    }),
                                    ...(subscriptionStatus.color === 'warning' && {
                                      backgroundColor: 'rgba(245,158,11,0.15)',
                                      color: 'rgb(245,158,11)',
                                      borderColor: 'rgba(245,158,11,0.3)'
                                    }),
                                    ...(subscriptionStatus.color === 'error' && {
                                      backgroundColor: 'rgba(239,68,68,0.15)',
                                      color: 'rgb(239,68,68)',
                                      borderColor: 'rgba(239,68,68,0.3)'
                                    })
                                  }}
                                />
                              );
                            })()}
                          </Box>
                        </StyledMenuItem>
                      );
                    })}
                  <Divider sx={{
                    mx: 1,
                    borderColor: theme ? '#e2e8f0' : '#334155'
                  }} />
                </>
              )}

              {/* Active Companies */}
              {companyList.filter(item => (!item?.demo && !item?.pendingApproval)).length > 0 && (
                <>
                  <Box sx={{
                    px: 2,
                    py: 1,
                    backgroundColor: theme ? '#f1f5f9' : '#0f172a'
                  }}>
                    <Typography variant="caption" sx={{
                      fontWeight: 700,
                      color: theme ? '#475569' : '#cbd5e1',
                      textTransform: 'uppercase',
                      letterSpacing: 1
                    }}>
                      Your Companies
                    </Typography>
                  </Box>
                    {companyList.filter(item => (!item?.demo && !item?.pendingApproval)).map((company) => {
                      const isValid = isCompanySubscriptionValid(company);
                      return (
                    <StyledMenuItem
                      key={company._id}
                      onClick={() => handleCompanySwitch(company._id)}
                      selected={currentCompany?.id === company._id}
                      isLightTheme={theme}
                          disabled={!isValid}
                    >
                      <ListItemIcon>
                        <Avatar
                          src={company.logo}
                          sx={{
                            width: 40,
                            height: 40,
                            mr: 1,
                            borderRadius: 0,
                            '& img': {
                              width: '100%',
                              height: '100%',
                              objectFit: 'contain',
                              borderRadius: 0
                            }
                          }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="body1" sx={{ fontWeight: 600, color: theme ? '#1e293b' : '#f1f5f9' }}>
                            {toTitleCase(company.company_name)}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="body2" sx={{ color: theme ? '#64748b' : '#94a3b8' }}>
                            {company.company_tagline}
                          </Typography>
                        }
                      />
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                        {company.isConnectedUser && (
                          <Chip
                            label="Personal"
                            size="small"
                            sx={{
                              backgroundColor: 'rgba(34,197,94,0.1)',
                              color: 'rgb(34,197,94)',
                              fontWeight: 600,
                              fontSize: '10px'
                            }}
                          />
                        )}
                            {(() => {
                              const subscriptionStatus = getCompanySubscriptionStatus(company);
                              return (
                                <Chip
                                  label={subscriptionStatus.label}
                                  size="small"
                                  color={subscriptionStatus.color as any}
                                  variant={subscriptionStatus.status === 'active' ? 'filled' : 'outlined'}
                                  sx={{
                                    fontWeight: 600,
                                    fontSize: '10px',
                                    ...(subscriptionStatus.color === 'success' && {
                                      backgroundColor: 'rgba(34,197,94,0.15)',
                                      color: 'rgb(34,197,94)',
                                      borderColor: 'rgba(34,197,94,0.3)'
                                    }),
                                    ...(subscriptionStatus.color === 'warning' && {
                                      backgroundColor: 'rgba(245,158,11,0.15)',
                                      color: 'rgb(245,158,11)',
                                      borderColor: 'rgba(245,158,11,0.3)'
                                    }),
                                    ...(subscriptionStatus.color === 'error' && {
                                      backgroundColor: 'rgba(239,68,68,0.15)',
                                      color: 'rgb(239,68,68)',
                                      borderColor: 'rgba(239,68,68,0.3)'
                                    })
                                  }}
                                />
                              );
                            })()}
                        {currentCompany?.id === company._id && (
                          <Chip
                            label="Current"
                            size="small"
                            sx={{
                              backgroundColor: 'rgba(90,192,234,0.1)',
                              color: 'rgb(90,192,234)',
                              fontWeight: 600
                            }}
                          />
                        )}
                      </Box>
                    </StyledMenuItem>
                      );
                    })}
                  <Divider sx={{
                    mx: 1,
                    borderColor: theme ? '#e2e8f0' : '#334155'
                  }} />
                </>
              )}

              {/* Demo Companies */}
              {companyList.filter(item => item?.demo).length > 0 && (
                <>
                  <Box sx={{
                    px: 2,
                    py: 1,
                    backgroundColor: theme ? '#f1f5f9' : '#0f172a'
                  }}>
                    <Typography variant="caption" sx={{
                      fontWeight: 700,
                      color: theme ? '#475569' : '#cbd5e1',
                      textTransform: 'uppercase',
                      letterSpacing: 1
                    }}>
                      Demo Companies
                    </Typography>
                  </Box>
                    {companyList.filter(item => item?.demo).map((company) => {
                      const isValid = isCompanySubscriptionValid(company);
                      return (
                    <StyledMenuItem
                      key={company._id}
                      onClick={() => handleCompanySwitch(company._id)}
                      selected={currentCompany?.id === company._id}
                      isLightTheme={theme}
                          disabled={!isValid}
                    >
                      <ListItemIcon>
                        <Avatar
                          src={company.logo}
                          sx={{
                            width: 40,
                            height: 40,
                            mr: 1,
                            borderRadius: 0,
                            '& img': {
                              width: '100%',
                              height: '100%',
                              objectFit: 'contain',
                              borderRadius: 0
                            }
                          }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="body1" sx={{ fontWeight: 600, color: theme ? '#1e293b' : '#f1f5f9' }}>
                            {toTitleCase(company.company_name)}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="body2" sx={{ color: theme ? '#64748b' : '#94a3b8' }}>
                            {company.company_tagline}
                          </Typography>
                        }
                      />
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                        {company.isConnectedUser ? (
                          <Chip
                            label="Personal"
                            size="small"
                            sx={{
                              backgroundColor: 'rgba(34,197,94,0.1)',
                              color: 'rgb(34,197,94)',
                              fontWeight: 600,
                              fontSize: '10px'
                            }}
                          />
                        ) : (
                            <Chip
                              label="DEMO"
                              size="small"
                              sx={{
                                backgroundColor: 'rgba(90,192,234,0.9)',
                                color: 'white',
                                fontWeight: 700,
                                fontSize: '10px'
                              }}
                            />
                        )}
                            {(() => {
                              const subscriptionStatus = getCompanySubscriptionStatus(company);
                              return (
                                <Chip
                                  label={subscriptionStatus.label}
                                  size="small"
                                  color={subscriptionStatus.color as any}
                                  variant={subscriptionStatus.status === 'active' ? 'filled' : 'outlined'}
                                  sx={{
                                    fontWeight: 600,
                                    fontSize: '10px',
                                    ...(subscriptionStatus.color === 'success' && {
                                      backgroundColor: 'rgba(34,197,94,0.15)',
                                      color: 'rgb(34,197,94)',
                                      borderColor: 'rgba(34,197,94,0.3)'
                                    }),
                                    ...(subscriptionStatus.color === 'warning' && {
                                      backgroundColor: 'rgba(245,158,11,0.15)',
                                      color: 'rgb(245,158,11)',
                                      borderColor: 'rgba(245,158,11,0.3)'
                                    }),
                                    ...(subscriptionStatus.color === 'error' && {
                                      backgroundColor: 'rgba(239,68,68,0.15)',
                                      color: 'rgb(239,68,68)',
                                      borderColor: 'rgba(239,68,68,0.3)'
                                    })
                                  }}
                                />
                              );
                            })()}
                        {currentCompany?.id === company._id && (
                          <Chip
                            label="Current"
                            size="small"
                            sx={{
                              backgroundColor: 'rgba(90,192,234,0.1)',
                              color: 'rgb(90,192,234)',
                              fontWeight: 600
                            }}
                          />
                        )}
                      </Box>
                    </StyledMenuItem>
                      );
                    })}
                  <Divider sx={{
                    mx: 1,
                    borderColor: theme ? '#e2e8f0' : '#334155'
                  }} />
                </>
              )}
            </>
          )}



          </MenuList>
        </Menu>
      </Box>
    </ErrorBoundary>
  );
};
export default CompanySelector;